import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ref, onValue, query, orderByChild, limitToLast } from 'firebase/database';
import { database } from '../firebase';
import { setWishes, setTotalCount } from '../store/wishesSlice';

const LanternDisplay = ({ newWish, onBack }) => {
  const [selectedWish, setSelectedWish] = useState(null);
  const [lanternPositions, setLanternPositions] = useState({});
  const [showOtherLanterns, setShowOtherLanterns] = useState(false);
  const [displayWishes, setDisplayWishes] = useState([]);
  const { wishes, totalCount } = useSelector((state) => state.wishes);
  const dispatch = useDispatch();

  // Color palettes for lanterns
  const colorPalettes = [
    { primary: '#FFD700', secondary: '#FF8C00', accent: '#FF4500' }, // Gold/Orange
    { primary: '#FF69B4', secondary: '#FF1493', accent: '#DC143C' }, // Pink/Red
    { primary: '#00CED1', secondary: '#20B2AA', accent: '#008B8B' }, // Cyan/Teal
    { primary: '#9370DB', secondary: '#8A2BE2', accent: '#4B0082' }, // Purple
    { primary: '#32CD32', secondary: '#228B22', accent: '#006400' }, // Green
    { primary: '#FF6347', secondary: '#FF4500', accent: '#B22222' }, // Tomato/Red
    { primary: '#87CEEB', secondary: '#4682B4', accent: '#191970' }, // Sky Blue
    { primary: '#DDA0DD', secondary: '#DA70D6', accent: '#8B008B' }, // Plum
  ];

  useEffect(() => {
    if (newWish) {
      // Show other lanterns after 1 second when there's a new wish
      const timer = setTimeout(() => {
        setShowOtherLanterns(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Show all lanterns immediately if no new wish
      setShowOtherLanterns(true);
    }
  }, [newWish]);

  useEffect(() => {
    const wishesQuery = query(
      ref(database, 'wishes'),
      orderByChild('timestamp'),
      limitToLast(40)
    );

    const unsubscribe = onValue(wishesQuery, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const wishesArray = Object.values(data).reverse();
        dispatch(setWishes(wishesArray));
        
        // Update display wishes gradually
        setDisplayWishes(prevDisplay => {
          const newWishes = wishesArray.filter(wish => 
            !prevDisplay.some(existing => existing.id === wish.id)
          );
          
          if (newWishes.length > 0) {
            const updated = [...newWishes, ...prevDisplay];
            // Keep only 50 wishes, remove oldest gradually
            return updated.slice(0, 50);
          }
          
          return prevDisplay;
        });
      }
    });

    const countRef = ref(database, 'wishes');
    const countUnsubscribe = onValue(countRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        dispatch(setTotalCount(Object.keys(data).length));
      }
    });

    return () => {
      unsubscribe();
      countUnsubscribe();
    };
  }, [dispatch]);

  useEffect(() => {
    if (displayWishes.length > 0) {
      const newPositions = { ...lanternPositions };
      const minDistance = 10;
      
      displayWishes.forEach(wish => {
        if (!newPositions[wish.id]) {
          let attempts = 0;
          let position;
          
          do {
            position = {
              x: Math.random() * 85 + 5,
              y: Math.random() * 60 + 20,
              size: Math.random() * 45 + 55,
              rotation: Math.random() * 20 - 10,
              colorIndex: Math.floor(Math.random() * colorPalettes.length)
            };
            attempts++;
          } while (
            attempts < 50 && 
            Object.values(newPositions).some(existing => {
              const dx = position.x - existing.x;
              const dy = position.y - existing.y;
              return Math.sqrt(dx * dx + dy * dy) < minDistance;
            })
          );
          
          newPositions[wish.id] = position;
        }
      });
      
      setLanternPositions(newPositions);
    }
  }, [displayWishes]);

  return (
    <div className="min-h-screen bg-black overflow-hidden relative">
      {/* Header */}
      <motion.header
        className="absolute top-0 left-0 right-0 bg-black/50 backdrop-blur-sm text-white h-32 flex items-center justify-center px-4 z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 1 }}
      >
        <div className="">
          <button
            onClick={() => onBack ? onBack() : window.history.back()}
            className="hover:scale-105 transition-transform"
          >
            <motion.img
              src="/thadin.png"
              alt="Thadingyut Festival"
              className="w-full h-auto max-w-xs md:max-w-sm lg:max-w-md"
            />
          </button>
        </div>
      </motion.header>

      {/* Sparkling Stars */}
      {Array.from({ length: 100 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute bg-white rounded-full"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${Math.random() * 3 + 1}px`,
            height: `${Math.random() * 3 + 1}px`,
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [0.5, 1.2, 0.5],
          }}
          transition={{
            duration: 2 + Math.random(),
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* New Wish Lantern (appears first) */}
      {newWish && (
        <motion.div
          className="absolute cursor-pointer"
          style={{
            left: '50%',
            top: '50%',
            transform: 'translateX(-50%)',
          }}
          initial={{ y: window.innerHeight + 100, opacity: 0, scale: 0.5 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          transition={{
            duration: 5,
            ease: "easeOut"
          }}
          whileHover={{ scale: 1.2 }}
          onClick={() => {
            const colors = colorPalettes[0];
            setSelectedWish({...newWish, colors, hueRotate: 0});
          }}
        >
          <motion.div
            animate={{
              y: [0, -15, 0],
              rotate: [0, 3, -3, 0],
              x: [0, 5, -5, 0]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <motion.img
              src="/lantern1.svg"
              alt="Your Wish Lantern"
              style={{
                width: '80px',
                height: '96px',
                filter: `drop-shadow(0 0 15px #FFD70070) drop-shadow(0 0 30px #FFD70040)`
              }}
              animate={{
                filter: [
                  `drop-shadow(0 0 15px #FFD70070) drop-shadow(0 0 30px #FFD70040)`,
                  `drop-shadow(0 0 20px #FFD70080) drop-shadow(0 0 40px #FFD70050)`,
                  `drop-shadow(0 0 15px #FFD70070) drop-shadow(0 0 30px #FFD70040)`
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            
            <svg
              width="80"
              height="96"
              viewBox="0 0 100 120"
              style={{ display: 'none' }}
              className="drop-shadow-lg"
            >
              <line x1="50" y1="5" x2="50" y2="15" stroke="#8B4513" strokeWidth="2"/>
              <ellipse cx="50" cy="15" rx="25" ry="8" fill="#FFD700" stroke="#FF8C00" strokeWidth="2"/>
              <ellipse cx="50" cy="12" rx="20" ry="5" fill="#FF8C00" opacity="0.8"/>
              <path
                d="M25 20 Q25 15 30 15 L70 15 Q75 15 75 20 L75 75 Q75 85 65 90 L35 90 Q25 85 25 75 Z"
                fill="url(#newWishGradient)"
                stroke="#FF4500"
                strokeWidth="2"
              />
              <rect x="32" y="25" width="36" height="45" fill="none" stroke="#FFF" strokeWidth="1" opacity="0.7" rx="8"/>
              <line x1="38" y1="30" x2="38" y2="65" stroke="#FF4500" strokeWidth="1" opacity="0.9"/>
              <line x1="50" y1="30" x2="50" y2="65" stroke="#FF4500" strokeWidth="1" opacity="0.9"/>
              <line x1="62" y1="30" x2="62" y2="65" stroke="#FF4500" strokeWidth="1" opacity="0.9"/>
              <line x1="32" y1="40" x2="68" y2="40" stroke="#FF4500" strokeWidth="1" opacity="0.7"/>
              <line x1="32" y1="55" x2="68" y2="55" stroke="#FF4500" strokeWidth="1" opacity="0.7"/>
              <rect x="48" y="90" width="4" height="8" fill="#8B0000" rx="2"/>
              <circle cx="50" cy="102" r="6" fill="#DC143C" stroke="#8B0000" strokeWidth="1"/>
              <circle cx="50" cy="102" r="4" fill="#FF6347" opacity="0.8"/>
              <g opacity="0.9">
                <line x1="45" y1="108" x2="44" y2="115" stroke="#8B0000" strokeWidth="1"/>
                <line x1="47" y1="108" x2="46" y2="116" stroke="#8B0000" strokeWidth="1"/>
                <line x1="50" y1="108" x2="50" y2="117" stroke="#8B0000" strokeWidth="1.5"/>
                <line x1="53" y1="108" x2="54" y2="116" stroke="#8B0000" strokeWidth="1"/>
                <line x1="55" y1="108" x2="56" y2="115" stroke="#8B0000" strokeWidth="1"/>
              </g>
              <circle cx="44" cy="113" r="1" fill="#DC143C"/>
              <circle cx="46" cy="114" r="1" fill="#DC143C"/>
              <circle cx="50" cy="115" r="1.5" fill="#DC143C"/>
              <circle cx="54" cy="114" r="1" fill="#DC143C"/>
              <circle cx="56" cy="113" r="1" fill="#DC143C"/>
              <defs>
                <linearGradient id="newWishGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFD700"/>
                  <stop offset="50%" stopColor="#FF8C00"/>
                  <stop offset="100%" stopColor="#FF4500"/>
                </linearGradient>
              </defs>
            </svg>
          </motion.div>
        </motion.div>
      )}

      {/* Other Lanterns (appear after delay) */}
      {showOtherLanterns && displayWishes.filter(wish => 
        !newWish || wish.id !== newWish.id
      ).map((wish) => {
        const position = lanternPositions[wish.id];
        if (!position) return null;

        const colors = colorPalettes[position.colorIndex];

        return (
          <motion.div
            key={wish.id}
            className="absolute cursor-pointer"
            style={{
              left: `${position.x}%`,
              top: `${position.y}%`,
            }}
            initial={{ y: window.innerHeight + 100, opacity: 0, scale: 0.5 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            transition={{
              duration: 11,
              delay: 1 + (Object.keys(lanternPositions).indexOf(wish.id) * 0.3),
              ease: "easeOut"
            }}
            whileHover={{ scale: 1.2 }}
            onClick={() => {
              const hueRotatedColors = {
                primary: colors.primary,
                secondary: colors.secondary,
                accent: colors.accent
              };
              setSelectedWish({...wish, colors: hueRotatedColors, hueRotate: position.colorIndex * 45});
            }}
          >
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [position.rotation, position.rotation + 2, position.rotation - 2, position.rotation]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {/* Custom SVG Lantern */}
              <motion.img
                src="/lantern1.svg"
                alt="Myanmar Lantern"
                style={{
                  width: `${position.size}px`,
                  height: `${position.size * 1.2}px`,
                  filter: `drop-shadow(0 0 10px ${colors.primary}70) drop-shadow(0 0 20px ${colors.primary}40) hue-rotate(${position.colorIndex * 45}deg)`
                }}
                animate={{
                  filter: [
                    `drop-shadow(0 0 10px ${colors.primary}70) drop-shadow(0 0 20px ${colors.primary}40) hue-rotate(${position.colorIndex * 45}deg)`,
                    `drop-shadow(0 0 14px ${colors.primary}80) drop-shadow(0 0 28px ${colors.primary}50) hue-rotate(${position.colorIndex * 45}deg)`,
                    `drop-shadow(0 0 10px ${colors.primary}70) drop-shadow(0 0 20px ${colors.primary}40) hue-rotate(${position.colorIndex * 45}deg)`
                  ]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />

            </motion.div>
          </motion.div>
        );
      })}

      {/* Simple Modal */}
      <AnimatePresence>
        {selectedWish && (
          <motion.div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setSelectedWish(null)}
          >
            <motion.div
              className="bg-white rounded-lg p-4 md:p-6 lg:p-8 w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg text-center relative"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src="/simee.png" 
                alt="Lantern" 
                className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 mx-auto mb-4 md:mb-6 rounded-lg"
              />
              <div>
                <h2 className="text-gray-800 mb-4 md:mb-6 text-sm md:text-base lg:text-lg leading-relaxed">"{selectedWish.wish}"</h2>
                <button
                  onClick={() => setSelectedWish(null)}
                  className="w-24 py-2 md:py-3 bg-pink-600 hover:bg-pink-700 rounded text-sm md:text-base transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <motion.footer
        className="absolute bottom-0 left-0 right-0 bg-black/30 backdrop-blur-sm text-white text-center py-3 md:py-4 px-4 z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 1 }}
      >
        <p className="text-xs md:text-sm text-white/80">
          ✨ “Even the smallest spark can shine” ✨
        </p>
      </motion.footer>
    </div>
  );
};

export default LanternDisplay;
